import { useTranslation } from 'react-i18next';

import {
  ActionAggregate,
  RequirementAggregate,
  Site,
  Theme,
  useGetLatestSyncQuery,
  User,
} from '~/schema';
import { formatUserFullname } from '~/utils/user';
import { ROLE_SLUGS } from '~/constants';
import capitalize from '~/utils/string';
import CardHeader from '../common/card-header.component';
import Icon from '../common/icon.component';
import RequirementStatusAggregation from './requirement-status-aggregation.component';
import HasRole from '../auth/has-role.component';

interface SiteAuditListItemProps {
  isOutdated: boolean;
  site: {
    id: string;
    name: string;
  };
  audit?: {
    id: string;
    name: string;
    quarter: string;
    year: string;
    auditor?: Partial<User>;
    sites: Partial<Site>[];
    themes: Partial<Theme>[];
    requirementAggregates: Partial<RequirementAggregate>[];
    actionAggregates: Partial<ActionAggregate>[];
  };
  onCreate?: ({ auditId }: { auditId?: string }) => void;
  onOpen?: () => void;
}

const SiteAuditListItem = ({
  site,
  audit,
  isOutdated,
  onOpen,
  onCreate,
}: SiteAuditListItemProps) => {
  const { t } = useTranslation();
  const { data } = useGetLatestSyncQuery();
  const { latestSync } = data ?? {};

  return (
    <div className="c-card mb-4">
      <CardHeader
        showDivider
        title={site.name}
        description={audit?.themes.map(({ name }) => name).join(' | ')}
      >
        {audit && (
          <HasRole
            requiredRole={[
              ROLE_SLUGS.ROOT,
              ROLE_SLUGS.TENANT_ADMINISTRATOR,
              ROLE_SLUGS.TENANT_USER,
              ROLE_SLUGS.COMPANY_ADMINISTRATOR,
              ROLE_SLUGS.COMPANY_USER,
            ]}
          >
            <button
              type="button"
              className="c-button--primary mb-2"
              onClick={onOpen}
            >
              {capitalize(t('bekijk actieve audit'))}
            </button>
          </HasRole>
        )}
      </CardHeader>
      {!audit && (
        <div className="min-h-[16rem] flex-grow flex items-center justify-center">
          <button
            type="button"
            className="c-button--primary"
            onClick={() => onCreate({})}
          >
            {capitalize(
              t(
                `start eerste audit (Q${latestSync?.quarter} ${latestSync?.year})`,
              ),
            )}
          </button>
        </div>
      )}
      {audit && (
        <>
          <div className="flex flex-col gap-2 items-stretch">
            <div className="flex flex-row gap-4">
              <div className="flex flex-row items-center">
                <Icon name="ClipboardListIcon" />
                <span>
                  {audit.quarter} {audit.year}
                </span>
              </div>
              <div className="flex flex-row items-center">
                <Icon name="UserIcon" />
                {audit.auditor ? formatUserFullname(audit.auditor) : '-'}
              </div>
            </div>
            <RequirementStatusAggregation
              className="flex-grow"
              items={audit.requirementAggregates}
            />
            {isOutdated && latestSync && (
              <div className="flex items-center justify-between">
                <div className="flex flex-row items-center">
                  <Icon name="ExclamationIcon" className="mr-2" />
                  <span>Audit Q{latestSync?.quarter} kan opgestart worden</span>
                </div>
                <button
                  type="button"
                  className="c-button--primary"
                  onClick={() => onCreate({ auditId: audit.id })}
                >
                  {capitalize(
                    t(`start Q${latestSync?.quarter} ${latestSync?.year}`),
                  )}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SiteAuditListItem;
